/**
 * Request Quote drawer utility.
 *
 * Public API (window.RQ):
 *   RQ.show()          — open the overview drawer
 *   RQ.close()         — close the overview drawer
 *   RQ.toggle()        — toggle open/closed
 *   RQ.isOpen()        — whether the drawer is currently open
 *   RQ.isStarted()     — whether the RFQ flow has been started this session
 *   RQ.markStarted()   — persist the RFQ started marker
 *   RQ.clearStarted()  — clear the RFQ started marker
 *   RQ.syncTriggers()  — update header trigger labels from started state
 *   RQ.syncCount()     — refresh quote item-count badges
 *   RQ.refresh()       — reload cart/step content into the drawer
 *   RQ.init()          — bind DOM once (auto-runs on DOMContentLoaded)
 */
(() => {
  const STARTED_KEY = 'austpek-rfq-started';
  const ROUTE_KEY = 'austpek-rfq-route';
  const PANEL_KEY = 'austpek-rfq-panel';
  const OPEN_CLASS = 'is-open';

  let root = null;
  let lastFocus = null;
  let bound = false;
  let itemCount = 0;
  let countPromise = null;

  /** Read a data-* label from the drawer root. */
  const label = (key) => (root && root.dataset[key]) || '';

  /** Escape text for safe HTML injection. */
  const escapeHtml = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  /** Format a Shopify cent amount with the shop money format when possible. */
  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    const format = label('moneyFormat') || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (error) {
        // Fall through to a simple formatter.
      }
    }
    const fixed = amount.toFixed(2);
    if (format.includes('{{amount_no_decimals}}')) {
      return format.replace('{{amount_no_decimals}}', String(Math.round(amount)));
    }
    return format.replace('{{amount}}', fixed).replace('{{amount_with_comma_separator}}', fixed.replace('.', ','));
  };

  /** Returns true when the RFQ flow was started (sessionStorage or cookie). */
  const isStarted = () => {
    try {
      if (sessionStorage.getItem(STARTED_KEY) === '1') return true;
    } catch (error) {
      // Fall through to cookie.
    }
    try {
      return /(?:^|;\s*)austpek-rfq-started=1(?:;|$)/.test(document.cookie);
    } catch (error) {
      return false;
    }
  };

  /** Persist that the shopper started the request-a-quote flow. */
  const markStarted = (route) => {
    try {
      sessionStorage.setItem(STARTED_KEY, '1');
      if (route) sessionStorage.setItem(ROUTE_KEY, route);
    } catch (error) {
      // Storage may be unavailable.
    }
    try {
      document.cookie = 'austpek-rfq-started=1;path=/;max-age=604800;SameSite=Lax';
    } catch (error) {
      // Cookie may be unavailable.
    }
    syncTriggers();
  };

  /** Clear the RFQ started marker from sessionStorage and cookie. */
  const clearStarted = () => {
    try {
      sessionStorage.removeItem(STARTED_KEY);
      sessionStorage.removeItem(ROUTE_KEY);
      sessionStorage.removeItem(PANEL_KEY);
    } catch (error) {
      // Ignore.
    }
    try {
      document.cookie = 'austpek-rfq-started=;path=/;max-age=0;SameSite=Lax';
    } catch (error) {
      // Ignore.
    }
    syncTriggers();
  };

  /** Read the current RFQ panel from session storage (products/project/contact). */
  const getPanel = () => {
    try {
      return sessionStorage.getItem(PANEL_KEY) || 'add';
    } catch (error) {
      return 'add';
    }
  };

  /** Read the current RFQ route from session storage (cart/add/build). */
  const getRoute = () => {
    try {
      return sessionStorage.getItem(ROUTE_KEY) || 'add';
    } catch (error) {
      return 'add';
    }
  };

  /** Map panel id → step number (1–3). */
  const panelToStep = (panel) => {
    if (panel === 'project') return 2;
    if (panel === 'contact') return 3;
    return 1;
  };

  /** Build public option strings from a cart line item. */
  const itemMeta = (item) => {
    const props = item.properties || {};
    const values = Object.keys(props)
      .filter((key) => key && key.charAt(0) !== '_' && props[key] != null && String(props[key]).trim() !== '')
      .map((key) => String(props[key]).trim())
      .filter((value) => !/^https?:\/\//i.test(value));
    if (values.length) return values.join(' · ');
    if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
    return '';
  };

  /** Fetch the current cart item count (cached request while in-flight). */
  const fetchItemCount = async () => {
    if (countPromise) return countPromise;
    countPromise = (async () => {
      try {
        const base = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${base}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return itemCount;
        const cart = await response.json();
        itemCount = Number(cart.item_count) || 0;
        return itemCount;
      } catch (error) {
        return itemCount;
      } finally {
        countPromise = null;
      }
    })();
    return countPromise;
  };

  /** Update Manage-my-quote count badges and Account attention dots. */
  const syncIndicators = (count = itemCount) => {
    const started = isStarted();
    const showCount = started && count > 0;
    const showDot = started;

    document.querySelectorAll('[data-rq-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = !showCount;
      badge.setAttribute('aria-hidden', showCount ? 'false' : 'true');
    });

    document.querySelectorAll('[data-rq-account-dot]').forEach((dot) => {
      dot.hidden = !showDot;
      dot.setAttribute('aria-hidden', showDot ? 'false' : 'true');
    });

    document.querySelectorAll('[data-rq-account-indicator]').forEach((el) => {
      el.classList.toggle('has-rq-activity', showDot);
    });
  };

  /** Update header trigger labels/aria for Request vs Manage, then sync badges. */
  const syncTriggers = () => {
    const started = isStarted();
    document.querySelectorAll('[data-rq-trigger]').forEach((trigger) => {
      const requestLabel = trigger.dataset.rqLabelRequest || 'Request a quote';
      const manageLabel = trigger.dataset.rqLabelManage || 'Manage my quote';
      const text = started ? manageLabel : requestLabel;
      const labelEl = trigger.querySelector('[data-rq-trigger-label]');
      if (labelEl) labelEl.textContent = text;
      else trigger.childNodes[0] && (trigger.childNodes[0].textContent = text);
      trigger.setAttribute('aria-label', text);
      trigger.setAttribute('data-rq-mode', started ? 'manage' : 'request');
    });
    syncIndicators(itemCount);
  };

  /** Refresh cart item count and update header indicators. */
  const syncCount = async () => {
    await fetchItemCount();
    syncIndicators(itemCount);
    return itemCount;
  };

  /** Returns whether the drawer is open. */
  const isOpen = () => Boolean(root && root.classList.contains(OPEN_CLASS));

  /** Close any open account popover so it does not sit above the drawer. */
  const closeAccountPopover = () => {
    const popover = document.getElementById('account-popover');
    if (popover && popover.getAttribute('aria-hidden') === 'false') {
      document.dispatchEvent(new CustomEvent('popover:close'));
      popover.setAttribute('aria-hidden', 'true');
      document.querySelectorAll('[aria-controls="account-popover"]').forEach((el) => {
        el.setAttribute('aria-expanded', 'false');
      });
    }
  };

  /** Apply step indicator UI from the saved panel. */
  const renderStep = () => {
    if (!root) return;
    const step = panelToStep(getPanel());
    const stepOf = label('labelStepOf')
      .replace('__CURRENT__', String(step))
      .replace('__TOTAL__', '3');
    let reviewed = label('labelStepProductsReviewed');
    let nextCopy = label('labelContinueNextProject');
    if (step === 2) {
      reviewed = label('labelStepProjectReviewed');
      nextCopy = label('labelContinueNextContact');
    } else if (step === 3) {
      reviewed = label('labelStepContactReviewed');
      nextCopy = label('labelContinueNextSubmit');
    }

    const stepCopy = root.querySelector('[data-rq-step-copy]');
    if (stepCopy) stepCopy.textContent = `${stepOf} • ${reviewed}`;

    root.querySelectorAll('[data-rq-progress-seg]').forEach((seg) => {
      const index = Number(seg.getAttribute('data-rq-progress-seg'));
      seg.classList.toggle('is-active', index <= step);
    });
    root.querySelectorAll('[data-rq-step-label]').forEach((el) => {
      const index = Number(el.getAttribute('data-rq-step-label'));
      el.classList.toggle('is-active', index === step);
    });

    const resumeNext = root.querySelector('[data-rq-resume-next]');
    if (resumeNext) resumeNext.textContent = nextCopy;

    const quoteUrl = root.dataset.quoteUrl || '/pages/request-a-quote';
    const route = getRoute();
    const reviewUrl = `${quoteUrl}?route=${encodeURIComponent(route === 'build' ? 'add' : route)}`;
    root.querySelectorAll('[data-rq-continue]').forEach((link) => {
      link.setAttribute('href', quoteUrl);
    });
    root.querySelectorAll('[data-rq-review]').forEach((link) => {
      link.setAttribute('href', reviewUrl);
    });
  };

  /** Render cart line items into the drawer list. */
  const renderItems = (cart) => {
    if (!root) return;
    const list = root.querySelector('[data-rq-items]');
    const listLabel = root.querySelector('[data-rq-list-label]');
    const subtotal = root.querySelector('[data-rq-subtotal]');
    if (!list) return;

    const items = (cart && cart.items) || [];
    const count = items.length;
    const countLabel =
      count === 1
        ? label('labelProductsOne')
        : label('labelProductsOther').replace('__COUNT__', String(count));

    if (listLabel) {
      listLabel.textContent = `${label('labelInQuote')} • ${countLabel}`.toUpperCase();
    }
    if (subtotal) subtotal.textContent = formatMoney(cart ? cart.total_price : 0);

    if (!count) {
      list.innerHTML = `<li class="rq-drawer__empty" data-rq-empty>${escapeHtml(label('labelEmpty'))}</li>`;
      return;
    }

    list.innerHTML = items
      .map((item) => {
        const meta = itemMeta(item);
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title || '')}" width="48" height="48" loading="lazy">`
          : '';
        return `
          <li class="rq-drawer__item">
            <div class="rq-drawer__thumb">${image}</div>
            <div class="rq-drawer__item-body">
              <div class="rq-drawer__item-title-row">
                <p class="rq-drawer__item-title">${escapeHtml(item.product_title || '')}</p>
                <span class="rq-drawer__check" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
                    <circle cx="7" cy="7" r="7" fill="currentColor"/>
                    <path d="M4 7.1 5.9 9l4.1-4.2" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </div>
              ${meta ? `<p class="rq-drawer__item-meta">${escapeHtml(meta)}</p>` : ''}
            </div>
          </li>
        `;
      })
      .join('');
  };

  /** Fetch cart.js and refresh drawer contents. */
  const refresh = async () => {
    if (!root) return;
    renderStep();
    try {
      const base = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${base}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      itemCount = Number(cart.item_count) || 0;
      renderItems(cart);
      syncIndicators(itemCount);
    } catch (error) {
      // Keep server-rendered markup if cart.js fails.
    }
  };

  /** Open the quote overview drawer. */
  const show = async () => {
    if (!root) return;
    lastFocus = document.activeElement;
    closeAccountPopover();
    await refresh();
    root.classList.add(OPEN_CLASS);
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('rq-drawer-open');
    const closeBtn = root.querySelector('.rq-drawer__close');
    if (closeBtn) closeBtn.focus();
  };

  /** Close the quote overview drawer. */
  const close = () => {
    if (!root || !isOpen()) return;
    root.classList.remove(OPEN_CLASS);
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('rq-drawer-open');
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
  };

  /** Toggle the drawer open or closed. */
  const toggle = () => {
    if (isOpen()) close();
    else show();
  };

  /** Handle clicks on header triggers (Request vs Manage). */
  const onTriggerClick = (event) => {
    const trigger = event.target.closest('[data-rq-trigger]');
    if (!trigger) return;
    if (!isStarted()) return; // Navigate to the quote start page.
    event.preventDefault();
    show();
  };

  /** Bind one-time listeners for triggers, close controls, and escape. */
  const bind = () => {
    if (bound) return;
    bound = true;
    document.addEventListener('click', onTriggerClick);
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-rq-close]')) {
        event.preventDefault();
        close();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) close();
    });
    document.addEventListener('product:added', () => {
      syncCount();
      if (isOpen()) refresh();
    });
    document.addEventListener('cart:refresh', () => {
      syncCount();
      if (isOpen()) refresh();
    });
  };

  /** Locate the drawer node and wire the public API. */
  const init = () => {
    root = document.querySelector('[data-rq-drawer]');
    // Keep the drawer outside sticky header/announcement stacking contexts.
    if (root && root.parentElement !== document.body) {
      document.body.appendChild(root);
    }
    bind();
    syncTriggers();
    syncCount();
    if (root) renderStep();
  };

  window.RQ = {
    /** Opens the quote overview drawer. */
    show,
    /** Closes the quote overview drawer. */
    close,
    /** Toggles the drawer open/closed. */
    toggle,
    /** Returns whether the drawer is currently open. */
    isOpen,
    /** Returns whether the RFQ flow has been started this session. */
    isStarted,
    /** Persists that the RFQ flow has started (optional route). */
    markStarted,
    /** Clears the RFQ started marker. */
    clearStarted,
    /** Updates header trigger labels from the started state. */
    syncTriggers,
    /** Refreshes cart item count badges on Manage my quote / Account. */
    syncCount,
    /** Reloads cart + step content inside the drawer. */
    refresh,
    /** Initializes DOM bindings (safe to call more than once). */
    init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
